/* @flow */

import * as React from 'react';
import styled from '@emotion/styled';
import { borderWidth, padding } from 'polished';
import numeral from 'numeral';

import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import Responsive from 'app/components/responsive/Responsive';

type Props = {
  avatar?: string,
  awayTeam?: string,
  average?: number,
  form?: number | string,
  gameOrder?: number,
  homeTeam?: string,
  initials: string,
  isHome: boolean,
  isRacing: boolean,
  isScratched?: boolean,
  labels?: Array<Object>,
  layout?: 'default' | 'reverse',
  name: string,
  place?: number,
  position: string,
  rider?: string,
  salary: number,
  trainer?: string,
  win?: number
};

const defaultProps = {
  awayTeam: '',
  avatar: undefined,
  average: undefined,
  form: undefined,
  gameOrder: undefined,
  homeTeam: '',
  isScratched: false,
  labels: [],
  layout: 'default',
  place: undefined,
  rider: undefined,
  trainer: undefined,
  win: undefined
};

const Wrapper = styled.div(
  ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    [theme.mediaQuery.tabletLandscapeAndBelow]: {
      padding: '0.5rem'
    }
  }),
  ({ layout }) =>
    layout === 'reverse'
      ? {
          flexDirection: 'row-reverse',
          textAlign: 'right'
        }
      : undefined
);

const Main = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  ...padding(0, '0.5rem'),
  [theme.mediaQuery.desktopAndUp]: {
    ...padding(0, '1rem')
  },
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    width: '100%',
    paddingRight: 0
  }
}));

const Detail = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
});

const Info = styled.div(({ theme }) => ({
  fontSize: '0.833em',
  fontWeight: theme.fontWeight.medium,
  paddingTop: '0.25em',
  paddingLeft: '0.25em',
  paddingRight: '0.5em',
  textTransform: 'uppercase',
  [theme.mediaQuery.tabletLandscapeAndBelow]: {
    alignSelf: 'flex-end',
    paddingBottom: theme.spacing.xsmall,
    paddingRight: 0,
    fontSize: '1em',
    fontWeight: theme.fontWeight.demiBold
  }
}));

const Row = styled.div(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  ({ reverse }) => (reverse ? { flexDirection: 'row-reverse' } : {})
);

const PlayerBarOuter = styled.div(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  '& > div': {
    paddingBottom: theme.spacing.tiny
  },
  borderColor: theme.color.basis5,
  borderStyle: 'solid',
  ...borderWidth(0, 0, 1),
  marginBottom: theme.spacing.tiny
}));

const Extra = styled.div({
  display: 'flex',
  marginTop: 'auto'
});

const SelectionLabel = styled.span(
  ({ reverse }) => ({
    alignItems: 'center',
    display: 'flex',
    fontSize: '0.833em',
    paddingLeft: reverse ? 0 : '2rem',
    paddingRight: reverse ? '2rem' : 0,
    textTransform: 'uppercase'
  }),
  ({ theme, type }) =>
    ({
      primary: {
        color: theme.color.primary
      },
      secondary: {
        color: theme.color.secondary
      }
    }[type])
);

const SelectionIcon = styled.span({
  display: 'inline-block',
  fontSize: '0.833em',
  marginRight: '0.5em'
});

const Stats = styled.div({
  paddingLeft: '0.25em',
  paddingRight: '0.6em',
  fontSize: '0.833em'
});

const StatsLabel = styled.span(({ theme }) => ({
  paddingRight: theme.spacing.tiny,
  fontWeight: theme.fontWeight.demiBold
}));

function PlayerSwapInfo({
  avatar,
  average,
  awayTeam,
  form,
  gameOrder,
  homeTeam,
  initials,
  isHome,
  isRacing,
  isScratched,
  labels,
  layout,
  name,
  place,
  position,
  rider,
  salary,
  trainer,
  win
}: Props) {
  return (
    <Wrapper layout={layout}>
      <Responsive desktop>
        {isLargeScreen => (
          <PlayerAvatar
            image={avatar}
            fallbackText={initials}
            size={isLargeScreen ? 'xlarge' : undefined}
            isRacing={isRacing}
          />
        )}
      </Responsive>
      <Main>
        <Responsive desktop>
          <Detail layout={layout}>
            <PlayerBarOuter>
              <PlayerBar
                average={average}
                awayTeam={awayTeam}
                form={form}
                gameOrder={gameOrder}
                homeTeam={homeTeam}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                name={name}
                position={position}
                layout={layout}
                rider={rider}
                trainer={trainer}
              />
            </PlayerBarOuter>
            <Row reverse={layout === 'reverse'}>
              {isRacing ? (
                <Info>
                  <span style={{ paddingRight: '1rem' }}>Win: {numeral(win).format('0.00')}</span>
                  <span>Place: {numeral(place).format('0.00')}</span>
                </Info>
              ) : (
                <Info>Salary: {numeral(salary).format('$0,0')}</Info>
              )}
              <div>
                <SelectionLabel
                  type={layout === 'default' ? 'primary' : 'secondary'}
                  reverse={layout === 'reverse'}
                >
                  <SelectionIcon
                    className={
                      layout === 'default'
                        ? 'ion-android-arrow-dropright-circle'
                        : 'ion-android-arrow-dropleft-circle'
                    }
                  />{' '}
                  {layout === 'default' ? 'Out' : 'In'}
                </SelectionLabel>
              </div>
            </Row>
          </Detail>
        </Responsive>
        <Responsive mobile>
          <Detail layout={layout}>
            <PlayerBarOuter>
              <PlayerBar
                awayTeam={awayTeam}
                homeTeam={homeTeam}
                gameOrder={gameOrder}
                isHome={isHome}
                isRacing={isRacing}
                isScratched={isScratched}
                name={name}
                position={position}
                labels={labels}
                layout={layout}
                rider={rider}
                trainer={trainer}
              />
              {!isRacing && <Info>{numeral(salary).format('$0,0')}</Info>}
            </PlayerBarOuter>
            <Row>
              <div style={{ display: 'flex' }}>
                {!isRacing && average !== undefined && (
                  <Stats paddingRight={layout === 'reverse'}>
                    <StatsLabel>AVG</StatsLabel>
                    <span>{numeral(average).format('0.0')}</span>
                  </Stats>
                )}
                {!isRacing && form !== undefined && (
                  <Stats paddingLeft={layout === 'default'}>
                    <StatsLabel>FORM</StatsLabel>
                    <span>{numeral(form).format('0.0')}</span>
                  </Stats>
                )}
                {win !== undefined && (
                  <Stats paddingRight={layout === 'reverse'}>
                    <StatsLabel>WIN</StatsLabel>
                    <span>{numeral(win).format('0.00')}</span>
                  </Stats>
                )}
                {place !== undefined && (
                  <Stats paddingLeft={layout === 'default'}>
                    <StatsLabel>PLACE</StatsLabel>
                    <span>{numeral(place).format('0.00')}</span>
                  </Stats>
                )}
              </div>
              <div>
                <SelectionLabel type={layout === 'default' ? 'primary' : 'secondary'}>
                  <SelectionIcon
                    className={
                      layout === 'default'
                        ? 'ion-android-arrow-dropright-circle'
                        : 'ion-android-arrow-dropleft-circle'
                    }
                  />{' '}
                  {layout === 'default' ? 'Out' : 'In'}
                </SelectionLabel>
              </div>
            </Row>
          </Detail>
        </Responsive>
        <Extra />
      </Main>
    </Wrapper>
  );
}

PlayerSwapInfo.defaultProps = defaultProps;

export default PlayerSwapInfo;
